import debug from 'debug'
import { type FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useAsync, useAsyncFn } from 'react-use'
import IcBaselineArrowBack from '~icons/ic/baseline-arrow-back'
import IcBaselineCancel from '~icons/ic/baseline-cancel'
import IcBaselineClose from '~icons/ic/baseline-close'
import IcBaselineOpenInNew from '~icons/ic/baseline-open-in-new'
import MdiBarcode from '~icons/mdi/barcode'
import MdiEmail from '~icons/mdi/email'
import MdiLoading from '~icons/mdi/loading'
import { UserCancelled } from 'src/components/Modal/Confirm'
import MarkdownRenderer from 'src/components/ui/MarkdownRenderer'
import Text from 'src/components/ui/Text'
import {
  executeCancelOrder,
  useGetOrderById,
} from 'src/services/apollo/payment'
import type {
  OpenPaymentDialogReturns,
  PaymentExecuteFn,
} from 'src/services/helpers/payment'
import { PaymentProvider } from 'src/services/helpers/payment'
import dayjs from 'src/utils/dayjs'
import { ConbiniBrand } from '../constants'

const log = debug('pixai:payment:gmo-cvs')

interface PaymentConfig {
  orderId: string
  receiptUrl?: string
  confNo: string
  receiptNo: string
  expiresAt: Date
  paymentMethod: ConbiniBrand
}

const CONBINI_OPTIONS = [
  {
    method: ConbiniBrand.SEVEN_ELEVEN,
    name: 'セブン-イレブン',
    logoUrl: '/conbini-logos/seven-eleven.png',
  },
  {
    method: ConbiniBrand.LAWSON,
    name: 'ローソン',
    logoUrl: '/conbini-logos/lawson.png',
  },
  {
    method: ConbiniBrand.FAMILY_MART,
    name: 'ファミリーマート',
    logoUrl: '/conbini-logos/family-mart.png',
  },
  {
    method: ConbiniBrand.MINI_STOP,
    name: 'ミニストップ',
    logoUrl: '/conbini-logos/mini-stop.gif',
  },
  {
    method: ConbiniBrand.SEICOMART,
    name: 'セイコーマート',
    logoUrl: '/conbini-logos/seicomart.png',
  },
] as const

const ConbiniSelector: FC<{
  onSelect: (method: ConbiniBrand) => void
}> = ({ onSelect }) => {
  return (
    <div className="w-full rounded-lg flex flex-col items-center justify-center text-black/75 text-center bg-white select-none px-4 py-8 gap-4">
      <div className="text-lg font-semibold mb-2">
        <Text i18nKey="membership:payment.gmo-cvs.select-conbini" />
      </div>
      <div className="w-full grid grid-cols-2 gap-2">
        {CONBINI_OPTIONS.map(option => (
          <button
            key={option.method}
            className="p-3 bg-neutral-100 hover:bg-neutral-200 active:bg-neutral-300 rounded-lg transition-colors flex flex-col items-center justify-center gap-2 min-h-[80px]"
            onClick={() => onSelect(option.method)}
          >
            <img
              src={option.logoUrl}
              alt={option.name}
              className="h-8 w-auto object-contain max-w-full"
              onError={e => {
                // Hide image on error, show text instead
                const target = e.target as HTMLImageElement
                target.style.display = 'none'
                const textElement = target.nextElementSibling as HTMLElement
                if (textElement) {
                  textElement.style.display = 'block'
                }
              }}
            />
            <div className="text-xs font-medium hidden">{option.name}</div>
          </button>
        ))}
      </div>
    </div>
  )
}

const PhoneNumberInput: FC<{
  paymentMethod: ConbiniBrand
  onSubmit: (phoneNumber: string) => void
  onBack: () => void
}> = ({ paymentMethod, onSubmit, onBack }) => {
  const { t } = useTranslation(['membership'])
  const [phoneNumber, setPhoneNumber] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Japanese phone number format: 070-1234-5678, 080-1234-5678, 090-1234-5678
  // Also accepts: 07012345678, 70-1234-5678
  const validatePhoneNumber = (value: string): boolean => {
    // Remove all hyphens and spaces for validation
    const cleaned = value.replace(/[-\s]/g, '')
    // Check if it matches Japanese mobile phone format (070/080/090)
    return /^0?(?:70|80|90)\d{4}\d{4}$/.test(cleaned)
  }

  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, '')
    // Format as 070-1234-5678
    if (cleaned.length <= 3) {
      return cleaned
    } else if (cleaned.length <= 7) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`
    } else {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7, 11)}`
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const formatted = formatPhoneNumber(value)
    setPhoneNumber(formatted)
    setError(null)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const cleaned = phoneNumber.replace(/[-\s]/g, '')
    if (!validatePhoneNumber(cleaned)) {
      setError(
        t('membership:payment.gmo-cvs.invalid-phone-number') ||
          '無効な電話番号です',
      )
      return
    }
    onSubmit(cleaned)
  }

  const selectedConbini = CONBINI_OPTIONS.find(
    option => option.method === paymentMethod,
  )
  const title = selectedConbini?.name || (
    <Text i18nKey="membership:payment.gmo-cvs.title" />
  )

  return (
    <div className="w-full rounded-lg flex flex-col items-center justify-center text-black/75 text-center bg-white select-none px-4 py-8 gap-4">
      {/* Header */}
      <div className="w-full flex items-center gap-2">
        <button
          className="text-gray-600 hover:text-gray-800 active:text-gray-900 p-2 -ml-2 transition-colors"
          onClick={onBack}
          aria-label={t('membership:payment.gmo-cvs.back-to-selection')}
        >
          <IcBaselineArrowBack className="w-5 h-5" />
        </button>
        <div className="text-lg font-semibold flex-1 text-center">{title}</div>
        <div className="w-6" />
      </div>

      {/* Phone number form */}
      <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4">
        <div className="text-left w-full">
          <label
            htmlFor="phone-number"
            className="block text-sm font-medium mb-2"
          >
            <Text i18nKey="membership:payment.gmo-cvs.phone-number-label" />
          </label>
          <input
            id="phone-number"
            type="tel"
            value={phoneNumber}
            onChange={handleChange}
            placeholder="070-1234-5678"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
            maxLength={13}
            autoComplete="tel"
          />
          <p className="mt-1 text-xs text-gray-500">
            <Text i18nKey="membership:payment.gmo-cvs.phone-number-hint" />
          </p>
          <p className="mt-2 text-xs text-gray-400 italic">
            <Text i18nKey="membership:payment.gmo-cvs.phone-number-privacy" />
          </p>
          {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        </div>

        <button
          type="submit"
          disabled={!phoneNumber.trim()}
          className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg px-4 py-2 font-medium transition-colors"
        >
          <Text i18nKey="membership:payment.gmo-cvs.continue" />
        </button>
      </form>
    </div>
  )
}

interface PaymentDialogContentGmoCVSProps {
  execute: PaymentExecuteFn
  trackingFnAfterFetch?: () => void
  onSucceeded?: (_: OpenPaymentDialogReturns) => void
  onFailed?: (error: Error) => void
}

export const PaymentDialogContentGmoCVS: FC<
  PaymentDialogContentGmoCVSProps
> = ({ execute, trackingFnAfterFetch, onSucceeded, onFailed }) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<ConbiniBrand | null>(null)
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null)
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null)

  const { error } = useAsync(async () => {
    if (!selectedPaymentMethod || !phoneNumber) {
      return
    }

    const result = await execute({
      provider: PaymentProvider.gmo,
      providerOptions: { paymentMethod: selectedPaymentMethod, phoneNumber },
    })

    setPaymentConfig({
      orderId: result.orderId,
      receiptUrl: result.url,
      confNo: result.confNo,
      receiptNo: result.receiptNo,
      expiresAt: new Date(result.expiredAt),
      paymentMethod: selectedPaymentMethod,
    })

    trackingFnAfterFetch?.()
  }, [selectedPaymentMethod, phoneNumber])

  useEffect(() => {
    if (error) {
      log('[Payment:GmoCVS] Failed to fetch payment configuration', error)
      onFailed?.(error)
    }
  }, [error, onFailed])

  if (!selectedPaymentMethod) {
    return <ConbiniSelector onSelect={setSelectedPaymentMethod} />
  }

  if (!phoneNumber) {
    return (
      <PhoneNumberInput
        paymentMethod={selectedPaymentMethod}
        onSubmit={setPhoneNumber}
        onBack={() => {
          setSelectedPaymentMethod(null)
          setPhoneNumber(null)
        }}
      />
    )
  }

  if (!paymentConfig) {
    return (
      <div className="h-[100px] w-full rounded-lg flex items-center duration-300 justify-center text-black/75 text-center text-sm bg-neutral-100 select-none">
        <MdiLoading className="size-6 animate-spin" />
      </div>
    )
  }

  const handleBack = () => {
    setSelectedPaymentMethod(null)
    setPhoneNumber(null)
    setPaymentConfig(null)
  }

  return (
    <PaymentDialogContentGmoCVSDisplay
      config={paymentConfig}
      onSucceeded={onSucceeded}
      onFailed={onFailed}
      onBack={handleBack}
    />
  )
}

const PaymentDialogContentGmoCVSDisplay: FC<{
  config: PaymentConfig
  onSucceeded?: (result: {
    orderId: string
    finalizedPaymentProvider: PaymentProvider
  }) => void
  onFailed?: (error: Error) => void
  onBack?: () => void
}> = ({ config, onSucceeded, onFailed, onBack }) => {
  const { t } = useTranslation(['membership'])
  const { data: order } = useGetOrderById(config.orderId)
  const [showReceiptFullscreen, setShowReceiptFullscreen] = useState(false)

  useEffect(() => {
    if (!showReceiptFullscreen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowReceiptFullscreen(false)
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [showReceiptFullscreen])

  const [cancelOrderState, cancelOrder] = useAsyncFn(async () => {
    try {
      await executeCancelOrder({ orderId: config.orderId })
      toast.success(
        <Text i18nKey="membership:payment.gmo-cvs.cancel-success" />,
      )
      onFailed?.(UserCancelled)
    } catch (err: any) {
      if (err === UserCancelled) {
        return
      }
      log('[Payment:GmoCVS] Failed to cancel order', err)
      toast.error(
        err?.message || (
          <Text i18nKey="membership:payment.gmo-cvs.cancel-error" />
        ),
      )
    }
  }, [config.orderId, onFailed, t])

  useEffect(() => {
    if (order?.status === 'completed') {
      log('[Payment:GmoCVS] Payment completed', order)
      onSucceeded?.({
        orderId: config.orderId,
        finalizedPaymentProvider: PaymentProvider.gmo,
      })
    } else if (order?.status === 'failed') {
      log('[Payment:GmoCVS] Payment failed', order)
      onFailed?.(
        new Error(t('membership:payment.failed-generic') || 'Payment failed'),
      )
    } else if (order?.status === 'cancelled') {
      log('[Payment:GmoCVS] Payment cancelled', order)
      onFailed?.(
        new Error(
          t('membership:payment.cancelled-due-to-timeout') ||
            'Payment cancelled due to timeout',
        ),
      )
    }
  }, [order, config.orderId, onSucceeded, onFailed, t])

  const isExpired = dayjs().isAfter(dayjs(config.expiresAt))
  const expiresAtFormatted = dayjs(config.expiresAt)
    .tz('Asia/Tokyo')
    .format('YYYY年MM月DD日 HH:mm')

  const selectedConbini = CONBINI_OPTIONS.find(
    option => option.method === config.paymentMethod,
  )
  const title = selectedConbini?.name || (
    <Text i18nKey="membership:payment.gmo-cvs.title" />
  )

  // Get button colors based on conbini brand colors
  const getButtonColors = () => {
    switch (config.paymentMethod) {
      case ConbiniBrand.SEVEN_ELEVEN:
        // Seven Eleven: Green
        return {
          bg: 'bg-green-600',
          hover: 'hover:bg-green-700',
          active: 'active:bg-green-800',
        }
      case ConbiniBrand.LAWSON:
        // Lawson: Blue
        return {
          bg: 'bg-blue-600',
          hover: 'hover:bg-blue-700',
          active: 'active:bg-blue-800',
        }
      case ConbiniBrand.FAMILY_MART:
        // FamilyMart: Blue
        return {
          bg: 'bg-blue-600',
          hover: 'hover:bg-blue-700',
          active: 'active:bg-blue-800',
        }
      case ConbiniBrand.MINI_STOP:
        // Mini Stop: Blue-Green
        return {
          bg: 'bg-teal-600',
          hover: 'hover:bg-teal-700',
          active: 'active:bg-teal-800',
        }
      case ConbiniBrand.SEICOMART:
        // Seicomart: Blue-Green
        return {
          bg: 'bg-cyan-600',
          hover: 'hover:bg-cyan-700',
          active: 'active:bg-cyan-800',
        }
      default:
        return {
          bg: 'bg-blue-600',
          hover: 'hover:bg-blue-700',
          active: 'active:bg-blue-800',
        }
    }
  }

  const buttonColors = getButtonColors()

  // Get payment instructions part 2 (shown below receipt number - only for receipt number method)
  const manualPaymentInstructions = (() => {
    switch (config.paymentMethod) {
      case ConbiniBrand.SEVEN_ELEVEN:
        return t('membership:payment.gmo-cvs.instructions.manual.seven-eleven')

      case ConbiniBrand.LAWSON:
        return t(`membership:payment.gmo-cvs.instructions.manual.lawson`)

      case ConbiniBrand.FAMILY_MART:
        return t('membership:payment.gmo-cvs.instructions.manual.family-mart')

      case ConbiniBrand.MINI_STOP:
        return t('membership:payment.gmo-cvs.instructions.manual.mini-stop')

      case ConbiniBrand.SEICOMART:
        return t('membership:payment.gmo-cvs.instructions.manual.seicomart')

      default:
        return t('membership:payment.gmo-cvs.instructions.manual.default')
    }
  })()

  const timeLimit = (
    <div className="text-xs opacity-70 mb-2">
      <Text
        i18nKey="membership:payment.gmo-cvs.expires-at"
        values={{ date: expiresAtFormatted }}
        components={[<span className="font-semibold" />]}
      />
    </div>
  )

  const isLoppi =
    config.paymentMethod === ConbiniBrand.LAWSON ||
    config.paymentMethod === ConbiniBrand.MINI_STOP
  const isFamilyMart = config.paymentMethod === ConbiniBrand.FAMILY_MART
  const isSeicomart = config.paymentMethod === ConbiniBrand.SEICOMART
  const showReceiptNoFirst = isLoppi || isSeicomart

  const confNoComponent = (
    <div className="w-full bg-neutral-100 rounded-lg p-4 space-y-3">
      <div className="text-sm font-medium">
        {isFamilyMart ? (
          <Text i18nKey="membership:payment.gmo-cvs.first-number" />
        ) : (
          <Text i18nKey="membership:payment.gmo-cvs.confirmation-number" />
        )}
      </div>
      <div className="text-2xl font-mono font-bold tracking-wider">
        {config.confNo}
      </div>
    </div>
  )
  const receiptNoComponent = (
    <div className="w-full bg-neutral-100 rounded-lg p-4 space-y-3">
      <div className="text-sm font-medium">
        {isLoppi ? (
          <Text i18nKey="membership:payment.gmo-cvs.customer-number" />
        ) : isFamilyMart ? (
          <Text i18nKey="membership:payment.gmo-cvs.second-number" />
        ) : (
          <Text i18nKey="membership:payment.gmo-cvs.receipt-number" />
        )}
      </div>
      <div className="text-2xl font-mono font-bold tracking-wider">
        {config.receiptNo}
      </div>
    </div>
  )

  return (
    <div className="w-full rounded-lg flex flex-col items-center duration-300 justify-center text-black/75 text-center bg-white select-none p-4 gap-4">
      {/* Header */}
      <div className="w-full flex items-center gap-2">
        {onBack && (
          <button
            className="text-gray-600 hover:text-gray-800 active:text-gray-900 p-2 -ml-2 transition-colors"
            onClick={onBack}
            aria-label={t('membership:payment.gmo-cvs.back-to-selection')}
          >
            <IcBaselineArrowBack className="w-5 h-5" />
          </button>
        )}
        <div className="text-lg font-semibold flex-1 text-center">{title}</div>
        {onBack && <div className="w-6" />}
      </div>

      {isExpired && (
        <div className="text-sm text-red-600">
          <Text i18nKey="membership:payment.gmo-cvs.expired" />
        </div>
      )}

      {isSeicomart && (
        <div className="w-full flex flex-col gap-2">
          <div className="w-full flex flex-col gap-2">
            <div className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-100 text-neutral-700">
              <div className="p-2 bg-white rounded-full shadow-sm shrink-0 text-blue-500">
                <MdiEmail className="w-5 h-5" />
              </div>
              <div className="text-xs font-medium text-left">
                <Text i18nKey="membership:payment.gmo-cvs.instructions.check-email" />
              </div>
            </div>
          </div>
          {timeLimit}
        </div>
      )}

      {!isExpired && (
        <>
          <div className="w-full flex flex-col items-center justify-center gap-4">
            <div className="flex items-start text-xs gap-2 opacity-70 mt-2">
              <MdiLoading className="size-4 animate-spin-slow shrink-0" />
              <span>
                <Text i18nKey="membership:payment.gmo-cvs.pending-payment" />
              </span>
            </div>
            {/* Payment processing time notice */}
            <div className="w-full flex items-start gap-2.5 p-3 bg-amber-50/80 border border-amber-300/50 rounded-lg">
              <div className="text-amber-600 shrink-0 mt-0.5 text-base leading-none font-bold">
                ℹ
              </div>
              <div className="text-xs text-amber-900 text-left flex-1 leading-relaxed">
                <Text i18nKey="membership:payment.gmo-cvs.processing-time-notice" />
              </div>
            </div>
            {/* Show receipt button first if available */}
            {config.receiptUrl && (
              <div className="w-full flex flex-col gap-2">
                <button
                  className={`text-sm text-white ${buttonColors.bg} rounded px-4 py-2 flex items-center justify-center gap-2 font-medium ${buttonColors.hover} ${buttonColors.active} select-none transition-colors`}
                  onClick={
                    config.paymentMethod === ConbiniBrand.FAMILY_MART
                      ? () => window.open(config.receiptUrl, '_blank')
                      : () => setShowReceiptFullscreen(true)
                  }
                >
                  <MdiBarcode className="w-5 h-5" />
                  <span>
                    <Text i18nKey="membership:payment.gmo-cvs.view-receipt" />
                  </span>
                  {config.paymentMethod === ConbiniBrand.FAMILY_MART && (
                    <IcBaselineOpenInNew className="w-4 h-4" />
                  )}
                </button>

                {showReceiptFullscreen &&
                  createPortal(
                    <div
                      className="fixed inset-0 z-[1500] bg-black/90 backdrop-blur-md flex flex-col"
                      onClick={e => {
                        // Close when clicking on the backdrop
                        if (e.target === e.currentTarget) {
                          setShowReceiptFullscreen(false)
                        }
                      }}
                    >
                      <div className="flex items-center justify-between p-4 bg-black/50 shrink-0">
                        <h2 className="text-white text-lg font-semibold">
                          <Text i18nKey="membership:payment.gmo-cvs.receipt-title" />
                        </h2>
                        <button
                          className="text-white bg-zinc-600 hover:bg-zinc-700 active:bg-zinc-800 ring-1 ring-black/20 p-2 rounded-full transition-colors"
                          onClick={() => setShowReceiptFullscreen(false)}
                          aria-label={t(
                            'membership:payment.gmo-cvs.close-receipt',
                          )}
                        >
                          <IcBaselineClose className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="flex-1 min-h-0 overflow-hidden">
                        <iframe
                          src={config.receiptUrl}
                          className="w-full h-full border-0 bg-white"
                          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                        />
                      </div>
                    </div>,
                    document.body,
                  )}
                {timeLimit}
                {/* Payment instructions part 1 shown below receipt button when available */}
                <div className="w-full flex flex-col gap-2">
                  <div className="text-xs font-medium text-left">
                    <Text i18nKey="membership:payment.gmo-cvs.instructions.payment-method" />
                  </div>
                  <div className="opacity-70 text-left w-full [&>p:not(:last-child)]:mb-2 text-xs">
                    <Text i18nKey="membership:payment.gmo-cvs.instructions.bar-code" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {(config.receiptUrl || isSeicomart) && (
            <div className="relative w-full flex items-center my-2">
              <div className="flex-1 h-px bg-black/10" />
              <span className="px-3 text-xs text-black/50">
                <Text i18nKey="membership:payment.gmo-cvs.or" />
              </span>
              <div className="flex-1 h-px bg-black/10" />
            </div>
          )}

          {/* Receipt number section at bottom */}
          {!isExpired && (
            <div className="w-full flex flex-col gap-2">
              {showReceiptNoFirst && receiptNoComponent}
              {config.paymentMethod !== ConbiniBrand.SEVEN_ELEVEN &&
                confNoComponent}
              {!showReceiptNoFirst && receiptNoComponent}
              {timeLimit}
              <div className="w-full flex flex-col gap-2">
                <div className="text-xs font-medium text-left">
                  <Text i18nKey="membership:payment.gmo-cvs.instructions.payment-method" />
                </div>
                <div className="opacity-70 text-left w-full [&>p:not(:last-child)]:mb-2 text-xs">
                  <MarkdownRenderer className="[&>ol]:list-decimal [&>ol]:pl-4 [&>ul]:list-disc [&>ul]:pl-4 [&>p>a]:text-blue-600 [&>p>a]:underline">
                    {manualPaymentInstructions}
                  </MarkdownRenderer>
                </div>
              </div>
            </div>
          )}

          {/* Cancel button at bottom */}
          {!isExpired && order?.status === 'unpaid' && (
            <div className="flex flex-col gap-2 w-full mt-4">
              <button
                className="text-sm text-gray-700 bg-gray-200 rounded px-4 py-2 flex items-center justify-center gap-2 font-medium hover:bg-gray-300 active:bg-gray-400 select-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={cancelOrder}
                disabled={cancelOrderState.loading}
              >
                {cancelOrderState.loading ? (
                  <>
                    <MdiLoading className="w-4 h-4 animate-spin" />
                    <span>
                      <Text i18nKey="membership:payment.gmo-cvs.cancelling" />
                    </span>
                  </>
                ) : (
                  <>
                    <IcBaselineCancel className="w-4 h-4" />
                    <span>
                      <Text i18nKey="membership:payment.gmo-cvs.cancel-order" />
                    </span>
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
